import React from "react"

import * as ModalModule from "../../shared/Modal"

type AnyProps = Record<string, unknown>
const AnimatedModal = ModalModule.AnimatedModal as unknown as React.FC<AnyProps>

type Action = "book" | "reschedule" | "accept"
type Variant = "conflict" | "unreachable"

type Props = {
  visible: boolean
  hideModal: () => void
  onConfirm: () => void
  disabled?: boolean
  // "book" (create), "reschedule", or "accept" — only changes copy.
  action?: Action
  // Known busy vs unreachable calendar. Default unreachable for back-compat.
  variant?: Variant
}

type ActionCopy = { header: string; button: string; verb: string }

const UNREACHABLE_COPY: Record<Action, ActionCopy> = {
  book: { header: "Calendar couldn't be verified", button: "Book anyway", verb: "book this appointment" },
  reschedule: {
    header: "Calendar couldn't be verified",
    button: "Reschedule anyway",
    verb: "move this appointment"
  },
  accept: { header: "Calendar couldn't be verified", button: "Accept anyway", verb: "accept this appointment" }
}

const CONFLICT_COPY: Record<Action, ActionCopy> = {
  book: { header: "This time has a conflict", button: "Book anyway", verb: "book this appointment" },
  reschedule: {
    header: "This time has a conflict",
    button: "Reschedule anyway",
    verb: "move this appointment"
  },
  accept: { header: "This time has a conflict", button: "Accept anyway", verb: "accept this appointment" }
}

const CONFLICT_BODY =
  "This time conflicts with an existing booking, Personal Time, or a busy time on a connected calendar. You can still continue if you want to."

// Shown when live verification found a known busy conflict, or could not reach a
// connected calendar (provider outage / reauthorization). Pro can acknowledge.
export default function CalendarOverrideConfirmModal({
  visible,
  hideModal,
  onConfirm,
  disabled = false,
  action = "book",
  variant = "unreachable"
}: Props) {
  const copyTable = variant === "conflict" ? CONFLICT_COPY : UNREACHABLE_COPY
  const copy = copyTable[action] ?? copyTable.book

  return (
    <AnimatedModal
      visible={visible}
      hideModal={hideModal}
      header={copy.header}
      showFooter={true}
      actionButtonCopy={copy.button}
      actionButtonDataTestid="calendar-override-confirm"
      saveDisabled={disabled}
      onSave={onConfirm}>
      {variant === "conflict" ? (
        <p>{CONFLICT_BODY}</p>
      ) : (
        <p>
          We couldn&apos;t reach one of your connected calendars to confirm this time is free. You can still {copy.verb},
          but it may overlap with an event on that calendar.
        </p>
      )}
    </AnimatedModal>
  )
}

export function overrideReasonToVariant(overrideReason?: string | null): Variant {
  return overrideReason === "conflict" ? "conflict" : "unreachable"
}
